import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { api } from "../api.js";
import {
  Badge, Button, EmptyState, Field, LoadingRows, Notice, PageHeader, SelectField,
  TabButton, Table, TableCell, TableHead, TableHeader, TableRow, TabList,
} from "../components/ui.jsx";

const TOOL_STATUSES = new Set(["running", "succeeded", "failed", "replayed"]);
const ENRICHMENT_STATUSES = new Set(["running", "succeeded", "failed", "no_result", "skipped"]);

function dateLabel(value) {
  if (!value) return "—";
  return new Intl.DateTimeFormat("en", { dateStyle: "medium", timeStyle: "short" }).format(new Date(value));
}

function durationLabel(value) {
  if (value == null) return "—";
  if (value < 1_000) return `${value} ms`;
  return `${(value / 1_000).toFixed(value < 10_000 ? 1 : 0)} s`;
}

function statusLabel(value) {
  return String(value || "unknown").replaceAll("_", " ");
}

function JsonBlock({ label, value }) {
  if (value == null) return null;
  return (
    <section className="grid min-w-0 gap-2">
      <h4 className="text-[10px] font-bold uppercase tracking-[.1em] text-muted-foreground">{label}</h4>
      <pre className="max-h-[360px] overflow-auto whitespace-pre-wrap break-words rounded-lg border border-neutral-100 bg-card p-4 font-mono text-[11px] leading-5 text-neutral-600">{JSON.stringify(value, null, 2)}</pre>
    </section>
  );
}

function Metric({ label, value, detail }) {
  return (
    <div className="grid min-h-[84px] content-center gap-1 border-b border-neutral-100 px-4 py-3 last:border-b-0 sm:border-b-0 sm:border-r sm:last:border-r-0">
      <span className="text-[10px] font-bold uppercase tracking-[.1em] text-muted-foreground">{label}</span>
      <span className="text-xl font-bold tabular-nums tracking-[-.03em] text-foreground">{value}</span>
      <span className="text-[11px] text-muted-foreground">{detail}</span>
    </div>
  );
}

function ToolCallDetail({ detail, loading }) {
  if (loading) return <LoadingRows count={2} />;
  if (!detail) return null;
  return (
    <div className="grid gap-5 bg-muted/60 p-4 lg:grid-cols-2">
      <div className="grid content-start gap-5">
        <div className="grid grid-cols-2 gap-px overflow-hidden rounded-lg border border-neutral-100 bg-neutral-100 text-xs">
          <div className="bg-card p-3"><span className="block text-[10px] uppercase tracking-[.08em] text-muted-foreground">Request ID</span><code className="break-all text-neutral-600">{detail.request_id}</code></div>
          <div className="bg-card p-3"><span className="block text-[10px] uppercase tracking-[.08em] text-muted-foreground">Duration</span><span className="tabular-nums text-neutral-600">{durationLabel(detail.duration_ms)}</span></div>
        </div>
        <JsonBlock label="Request" value={detail.request} />
      </div>
      <div className="grid content-start gap-5">
        <JsonBlock label="Response" value={detail.response} />
        <JsonBlock label="Error" value={detail.error} />
      </div>
    </div>
  );
}

function Attempt({ attempt }) {
  return (
    <article className="grid gap-4 bg-card p-4">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <span className="grid size-7 place-items-center rounded-full bg-muted text-[11px] font-bold tabular-nums text-muted-foreground">{attempt.sequence}</span>
          <div><h4 className="text-sm font-bold capitalize text-foreground">{attempt.provider}</h4><p className="text-[11px] text-muted-foreground">{attempt.provider_request_ref || "No provider request reference"}</p></div>
        </div>
        <div className="flex items-center gap-2"><Badge status={attempt.status}>{statusLabel(attempt.status)}</Badge><Badge>{attempt.credits_charged == null ? "credits not reported" : `${attempt.credits_charged} credits`}</Badge></div>
      </header>
      <div className="grid gap-4 lg:grid-cols-3">
        <JsonBlock label="Provider request" value={attempt.request} />
        <JsonBlock label="Provider result" value={attempt.result} />
        <JsonBlock label="Provider error" value={attempt.error} />
      </div>
    </article>
  );
}

function EnrichmentDetail({ detail, loading, applying, onApply }) {
  if (loading) return <LoadingRows count={3} />;
  if (!detail) return null;
  const canApply = detail.status === "succeeded"
    && detail.result?.field === "business_email"
    && detail.result?.value
    && !detail.application;
  return (
    <div className="grid gap-5 bg-muted/60 p-4">
      {detail.application ? (
        <Notice tone="info">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <span>Applied to CRM as <strong className="text-foreground">{detail.application.verification_status}</strong> on {dateLabel(detail.application.created_at)}.</span>
            <Badge status="succeeded">Applied</Badge>
          </div>
        </Notice>
      ) : canApply ? (
        <div className="flex flex-col gap-4 rounded-xl bg-card p-4 shadow-soft sm:flex-row sm:items-center sm:justify-between">
          <div className="grid gap-1">
            <h3 className="text-sm font-bold text-foreground">Accept this result</h3>
            <p className="max-w-[72ch] text-xs leading-5 text-muted-foreground">
              Save <strong className="text-neutral-600">{detail.result.value}</strong> to the canonical person as {detail.result.verification_status === "verified" ? "provider-verified" : "unverified"}. Duplicate and suppression checks run before CRM changes.
            </p>
          </div>
          <Button className="shrink-0" variant="primary" disabled={applying} onClick={onApply}>{applying ? "Applying…" : "Apply to CRM"}</Button>
        </div>
      ) : null}
      <div className="grid gap-4 lg:grid-cols-3">
        <JsonBlock label="Resolved request" value={detail.request} />
        <JsonBlock label="Selected result" value={detail.result} />
        <JsonBlock label="Run error" value={detail.error} />
      </div>
      <section className="grid gap-3">
        <div className="flex items-center justify-between gap-3"><h3 className="text-sm font-bold text-foreground">Provider waterfall</h3><span className="text-[11px] text-muted-foreground">{detail.attempts.length} recorded attempt{detail.attempts.length === 1 ? "" : "s"}</span></div>
        <div className="grid gap-px overflow-hidden rounded-xl border border-neutral-100 bg-neutral-100">{detail.attempts.map((attempt) => <Attempt attempt={attempt} key={attempt.id} />)}</div>
      </section>
    </div>
  );
}

export default function ActivityPage() {
  const [tab, setTab] = useState("tools");
  const [filters, setFilters] = useState({ search: "", status: "", provider: "" });
  const [state, setState] = useState({ loading: true, toolCalls: [], enrichmentRuns: [], error: "" });
  const [expanded, setExpanded] = useState(null);
  const [details, setDetails] = useState({});
  const [detailLoading, setDetailLoading] = useState(null);
  const [applying, setApplying] = useState(null);
  const [actionNotice, setActionNotice] = useState(null);

  const load = useCallback(async () => {
    setState((current) => ({ ...current, loading: true, error: "" }));
    try {
      const common = { limit: 200, ...(filters.search ? { search: filters.search } : {}) };
      const [toolData, enrichmentData] = await Promise.all([
        api.mcpActivity({ ...common, ...(TOOL_STATUSES.has(filters.status) ? { status: filters.status } : {}) }),
        api.enrichmentActivity({
          ...common,
          ...(ENRICHMENT_STATUSES.has(filters.status) ? { status: filters.status } : {}),
          ...(filters.provider ? { provider: filters.provider } : {}),
        }),
      ]);
      setState({ loading: false, toolCalls: toolData.tool_calls, enrichmentRuns: enrichmentData.enrichment_runs, error: "" });
    } catch (error) {
      setState((current) => ({ ...current, loading: false, error: error.message }));
    }
  }, [filters]);

  useEffect(() => { const timer = window.setTimeout(load, 180); return () => window.clearTimeout(timer); }, [load]);

  const metrics = useMemo(() => ({
    toolCalls: state.toolCalls.length,
    failed: state.toolCalls.filter((item) => item.status === "failed").length + state.enrichmentRuns.filter((item) => item.status === "failed").length,
    credits: state.enrichmentRuns.reduce((sum, item) => sum + item.credits_charged, 0),
    agents: new Set([
      ...state.toolCalls.map((item) => item.agent.name),
      ...state.enrichmentRuns.map((item) => item.requested_by.actor_name).filter(Boolean),
    ]).size,
  }), [state.enrichmentRuns, state.toolCalls]);

  async function openDetail(type, id) {
    const key = `${type}:${id}`;
    if (expanded === key) { setExpanded(null); return; }
    setExpanded(key);
    if (details[key]) return;
    setDetailLoading(key);
    try {
      const data = type === "tool" ? await api.mcpActivityDetail(id) : await api.enrichmentActivityDetail(id);
      setDetails((current) => ({ ...current, [key]: type === "tool" ? data.tool_call : data.enrichment_run }));
    } catch (error) {
      setState((current) => ({ ...current, error: error.message }));
    } finally {
      setDetailLoading(null);
    }
  }

  async function applyRun(id) {
    const key = `enrichment:${id}`;
    setApplying(id);
    setActionNotice(null);
    try {
      const response = await api.applyEnrichmentResult(id);
      const refreshed = await api.enrichmentActivityDetail(id);
      setDetails((current) => ({ ...current, [key]: refreshed.enrichment_run }));
      setState((current) => ({
        ...current,
        enrichmentRuns: current.enrichmentRuns.map((run) => (
          run.id === id ? { ...run, application: response.result.application } : run
        )),
      }));
      setActionNotice({ tone: "info", text: `${response.result.person.business_email} was applied to ${response.result.person.name} as ${response.result.person.email_verification_status}.` });
    } catch (error) {
      setActionNotice({ tone: "error", text: error.message });
    } finally {
      setApplying(null);
    }
  }

  const statusOptions = tab === "tools"
    ? [["", "All statuses"], ["succeeded", "Succeeded"], ["failed", "Failed"], ["running", "Running"], ["replayed", "Replayed"]]
    : [["", "All statuses"], ["succeeded", "Succeeded"], ["failed", "Failed"], ["no_result", "No result"], ["skipped", "Skipped"], ["running", "Running"]];

  return (
    <div className="grid gap-8">
      <PageHeader eyebrow="Administration" title="Agent activity" description="Inspect every recorded MCP tool call and every provider step in an enrichment waterfall, including inputs, results, failures, timing, and reported credits." action={<Button onClick={load}>Refresh</Button>} />
      {state.error && <Notice>{state.error}</Notice>}
      {actionNotice && <Notice tone={actionNotice.tone}>{actionNotice.text}</Notice>}
      <Notice tone="info">MCP call history starts with the activity migration. Earlier enrichment runs remain available because provider attempts were already stored.</Notice>

      <section className="grid overflow-hidden rounded-xl bg-card shadow-soft sm:grid-cols-4" aria-label="Activity summary">
        <Metric label="Tool calls" value={metrics.toolCalls} detail="matching current filters" />
        <Metric label="Failures" value={metrics.failed} detail="tools and enrichment" />
        <Metric label="Credits" value={metrics.credits} detail="reported by providers" />
        <Metric label="Agents" value={metrics.agents} detail="visible in this result" />
      </section>

      <section className="grid gap-4">
        <TabList className="justify-self-start" label="Activity type">
          {[["tools", "MCP tool calls", state.toolCalls.length], ["enrichment", "Enrichment", state.enrichmentRuns.length]].map(([id, label, count]) => (
            <TabButton key={id} selected={tab === id} onClick={() => { setTab(id); setExpanded(null); setFilters((current) => ({ ...current, status: "" })); }}>{label} <span className="ml-1 tabular-nums text-muted-foreground">{count}</span></TabButton>
          ))}
        </TabList>

        <div className="grid gap-3 rounded-xl bg-card p-4 shadow-soft md:grid-cols-[minmax(240px,1fr)_190px_190px_auto]">
          <Field label="Search" placeholder="Tool, agent, person, company, or run ID" value={filters.search} onChange={(event) => setFilters((current) => ({ ...current, search: event.target.value }))} />
          <SelectField label="Status" value={filters.status} onChange={(event) => setFilters((current) => ({ ...current, status: event.target.value }))}>{statusOptions.map(([value, label]) => <option value={value} key={value}>{label}</option>)}</SelectField>
          <SelectField label="Provider" value={filters.provider} disabled={tab === "tools"} onChange={(event) => setFilters((current) => ({ ...current, provider: event.target.value }))}><option value="">All providers</option><option value="apollo">Apollo</option><option value="lusha">Lusha</option><option value="origami">Origami</option></SelectField>
          {(filters.search || filters.status || filters.provider) && <Button className="min-h-8 justify-self-start self-end px-2.5 text-xs" variant="ghost" onClick={() => setFilters({ search: "", status: "", provider: "" })}>Clear filters</Button>}
        </div>

        {state.loading ? <LoadingRows count={6} /> : tab === "tools" ? (
          !state.toolCalls.length ? <EmptyState title="No MCP tool calls recorded" description="New agent tool calls will appear here with their request, result, timing, and error state." /> : (
            <div className="overflow-hidden rounded-xl bg-card shadow-soft"><Table><TableHead><TableHeader>Tool</TableHeader><TableHeader>Agent</TableHeader><TableHeader>Type</TableHeader><TableHeader>Status</TableHeader><TableHeader>Duration</TableHeader><TableHeader>Started</TableHeader></TableHead><tbody>
              {state.toolCalls.map((call) => {
                const key = `tool:${call.id}`;
                return <Fragment key={call.id}><TableRow className="cursor-pointer hover:bg-muted" onClick={() => openDetail("tool", call.id)}><TableCell><button className="max-w-[280px] truncate text-left font-bold text-foreground" type="button" aria-expanded={expanded === key}>{call.tool_name}</button><code className="mt-1 block max-w-[280px] truncate text-[10px] text-muted-foreground">{call.request_id}</code></TableCell><TableCell>{call.agent.name}</TableCell><TableCell><Badge>{call.tool_kind}</Badge></TableCell><TableCell><Badge status={call.status}>{statusLabel(call.status)}</Badge></TableCell><TableCell className="tabular-nums text-muted-foreground">{durationLabel(call.duration_ms)}</TableCell><TableCell className="whitespace-nowrap text-muted-foreground">{dateLabel(call.started_at)}</TableCell></TableRow>{expanded === key && <tr><td className="p-0" colSpan="6"><ToolCallDetail detail={details[key]} loading={detailLoading === key} /></td></tr>}</Fragment>;
              })}
            </tbody></Table></div>
          )
        ) : !state.enrichmentRuns.length ? <EmptyState title="No enrichment runs found" description="Adjust the filters or ask an authorized agent to run person enrichment through MCP." /> : (
          <div className="overflow-hidden rounded-xl bg-card shadow-soft"><Table><TableHead><TableHeader>Person</TableHeader><TableHeader>Waterfall</TableHeader><TableHeader>Status</TableHeader><TableHeader>Attempts</TableHeader><TableHeader>Credits</TableHeader><TableHeader>Started</TableHeader></TableHead><tbody>
            {state.enrichmentRuns.map((run) => {
              const key = `enrichment:${run.id}`;
              return <Fragment key={run.id}><TableRow className="cursor-pointer hover:bg-muted" onClick={() => openDetail("enrichment", run.id)}><TableCell><button className="max-w-[280px] truncate text-left font-bold text-foreground" type="button" aria-expanded={expanded === key}>{run.person?.name || "Deleted person"}</button><span className="mt-1 block max-w-[280px] truncate text-[11px] text-muted-foreground">{run.person?.title || "No title"}{run.company_name ? ` · ${run.company_name}` : ""}</span></TableCell><TableCell><span className="text-neutral-600">{run.provider_order.join(" → ")}</span><code className="mt-1 block max-w-[240px] truncate text-[10px] text-muted-foreground">{run.id}</code></TableCell><TableCell><div className="flex flex-wrap gap-1.5"><Badge status={run.status}>{statusLabel(run.status)}</Badge>{run.application && <Badge status="succeeded">Applied</Badge>}</div></TableCell><TableCell className="tabular-nums text-muted-foreground">{run.attempts_count}{run.failed_attempts ? <span className="ml-1 text-destructive">({run.failed_attempts} failed)</span> : null}</TableCell><TableCell className="tabular-nums text-muted-foreground">{run.credits_charged}</TableCell><TableCell className="whitespace-nowrap text-muted-foreground">{dateLabel(run.created_at)}</TableCell></TableRow>{expanded === key && <tr><td className="p-0" colSpan="6"><EnrichmentDetail detail={details[key]} loading={detailLoading === key} applying={applying === run.id} onApply={() => applyRun(run.id)} /></td></tr>}</Fragment>;
            })}
          </tbody></Table></div>
        )}
      </section>
    </div>
  );
}
